import { gql } from "@apollo/client";

export const Me = gql`
  query {
    me {
      id
      name
      email
      avatarUrl
      firstName
      lastName
      middleName
      sex
      dateOfBirth
      roles
      reports {
        id
        reportType {
          id
          name
        }
        data
        version
        locked
        needsReview
        createdAt
        updatedAt
      }
      kits {
        id
      }
      staffProfile {
        title
        specialty
        phone
        npi
        bio
        clinicName
        clinicAddressLine1
        clinicAddressLine2
        clinicAddressCity
        clinicAddressState
        clinicAddressPostalCode
        clinicAddressPostalCountry
      }
    }
  }
`

// Paged list of Members (patients) the current user can see.
// Replaces the legacy `me.assignedMembers` field. See TMP-2.
// Permission: ViewMembers — GLOBAL roles (Admin/StaffPhysician/Manager/Sales)
// see all customers; STAFF roles (Counselor/Dietician/Fellow/Pharmacist/Support)
// see only their explicit user_staff assignments. Physicians fall through to
// the assigned-only path via their user_staff rows.
export const GetMembers = gql`
  query GetMembers($first: Int, $after: String) {
    members(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          relationshipKind
          viewerRole
          assignedStaffRole
          assignedStaffNote
          user {
            id
            firstName
            lastName
            name
            dateOfBirth
            roles
            sex
            avatarUrl
            email
            phone
            createdAt
            reportSummary {
              inReviewCount
              totalCount
            }
            kits {
              accessionId
            }
            assignedStaff {
              id
              role
              staffId
              staff {
                avatarUrl
                name
                staffProfile {
                  title
                  specialty
                  phone
                  npi
                  bio
                }
              }
            }
            medications {
              name
              strength
              createdAt
            }
            staffProfile {
              phone
            }
            firstMeeting {
              id
              createdAt
              updatedAt
              paidAt
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

// Modify a user's name, sex, or DoB (the user themselves or an Admin can do this)
export const UpdateUser = gql`
  mutation ($user: UserInput!) {
    userUpdate (user: $user) {
      id
      name
      firstName
      middleName
      lastName
      sex
      dateOfBirth
      avatarUrl
    }
  }
`

// # Verify an uploaded file (for user avatars, family group avatars, etc.)
// # The ID is the ID returned from the type-specific upload mutation.
export const VerifyUpload = gql`
  mutation ($uploadId: Uuid!) {
    verifyUpload (uploadId: $uploadId)
  }
`

// # Update or Insert (Upsert) a Staff User's StaffProfile
export const UserSetStaffProfile = gql`
  mutation ($staffProfile: StaffProfileInput!) {
    userSetStaffProfile (staffProfile: $staffProfile) {
      bio
      npi
      phone
      specialty
      title
      clinicName
      clinicAddressLine1
      clinicAddressLine2
      clinicAddressCity
      clinicAddressState
      clinicAddressPostalCode
      clinicAddressPostalCountry
    }
  }
`

// # Upload a user avatar
// # Following this, you should upload the file to the URL with the provided method and headers and then call verifyUpload(id).
export const UploadUserAvatar = gql`
  mutation ($uploadInput: UploadInput!) {
    userUploadAvatar (uploadInput: $uploadInput) {
      id
      method
      url
      headers {
        key
        value
      }
    }
  }
`

// Auth
export const VerifyPhysicianCode = gql`
  query ($physicianCode: String!) {
    verifyPhysicianCode (physicianCode: $physicianCode)
  }
`

// Member
export const GetAllMembers = gql`
  query {
    allUsers {
      id
      name
      email
      roles
    }
  }
`;

export const GetMemberById = gql`
  query ($id: ID!) {
    member(id: $id) {
      id
      name
      email
      roles
    }
  }
`;

// # A new User becomes a Member by associating themselves to exactly one Kit, by using a Kit Code for
// # a valid (sold/fulfilled) Kit [Open, no auth required]
export const CreateMember = gql`
  mutation ($kitCode: String!, $memberData: MemberInput!) {
    memberRegister(kitCode: $kitCode, member: $memberData) {
      id
      name,
      firstName
      lastName
      middleName,
      sex,
      dateOfBirth
    }
  }
`;

export const UpdateMember = gql`
  mutation ($id: ID!, $name: String, $price: Int, $imageUrl: String) {
    updateUser(id: $id, name: $name, price: $price, imageUrl: $imageUrl) {
      id
      name
      price
      imageUrl
    }
  }
`;

export const DeleteMemberById = gql`
  mutation ($id: ID!) {
    deleteUser(id: $id) {
      id
    }
  }
`;

// Family
export const GetFamilyGroupUsers = gql`
  query {
    me {
      familyGroupUsers(first: 20) {
        familyGroups: edges {
          familyGroupUser: node {
            id
            role
            familyGroup: group {
              id
              name
              description
            }
          }
        }
      }
    }     
  }
`

export const CreateFamilyGroup = gql`
  mutation ($name: String!, $description: String!) {
    familyGroupCreate (name: $name, description: $description) {
      id
      name
      description
      createdAt
      avatarUrl
      users {
        edges {
          node {
            user {
              id
              email
              name
              firstName
              lastName
              middleName
              sex
              dateOfBirth
              roles
            }
          }
        }
      }
      persons {
        edges {
          node {
            id
            firstName
            lastName
            middleName
            sex
          }
        }
      }
    }
  }
`

//  Update the name/description of a family group
export const UpdateFamilyGroup = gql`
  mutation ($id: String!, $name: String!, $description: String!) {
    familyGroupUpdate(
      groupId: $id
      name: $name
      description: $description
    ) {
      id
      name
      description
    }
  }
`

// Set a family group user role
export const SetFamilyGroupUserRole = gql`
  mutation ($groupId: String!, $userId: String!, $role: String!) {
    familyGroupSetUserRole(
      groupId: $groupId
      userId: $userId
      role: $role
    ) {
      id
    }
  }
`

//  Remove a user from a family group
export const DeleteFamilyGroupUser = gql`
  mutation ($id: String!) {
    delete(id: $id)
  }
`

//  Add a Person to a family group with an attached user
export const CreateFamilyGroupPerson = gql`
  mutation ($groupId: String!, $person: Person!) {
    personCreate(
      familyGroupId: $groupId
      person: $person
    ) {
      id
      firstName
      middleName
      lastName
      dateOfBirth
      dateOfDeath
      sex
      user {
        id
      }
    }
  }
`

// Add a Person to a family group with no attached user
// mutation {
//   personCreate(
//     familyGroupId: "FamilyGroup:ID"
//     person: {
//       firstName: "Jane"
//       middleName: "Violence"
//       lastName: "Smith"
//       dateOfBirth: "1934-01-01"
//       dateOfDeath: "2011-07-11"
//       sex: FEMALE
//     }
//   ) {
//     id
//     firstName
//     middleName
//     lastName
//     dateOfBirth
//     dateOfDeath
//     sex
//     user {
//       id
//     }
//   }
// }

//  Update a Person to set a parent connection
export const UpdateFamilyGroupPerson = gql`
  mutation ($id: String!, $person: Person!) {
    personUpdate(personId: $id, person: $person) {
      id
      firstName
      middleName
      lastName
      dateOfBirth
      dateOfDeath
      sex
      family {
        id
        parent1 {
          id
        }
        parent2 {
          id
        }
      }
    }
  }
`

// Add a Person to a family group with both parents
export const AddFamilyGroupPersonWithBothParents = gql`
  mutation ($groupId: String!, $person: Person!) {
    personCreate(
      familyGroupId: $groupId
      person: $person
    ) {
      id
      firstName
      middleName
      lastName
      dateOfBirth
      dateOfDeath
      sex
      family {
        id
        parent1 {
          id
        }
        parent2 {
          id
        }
      }
    }
  }
`

// Import a GEDCOM file to a family group
export const ImportFamilyGroupGedcom = gql`
  mutation ($groupId: String!, $gedcomB64: String!) {
    familyGroupImportGedcom(
      familyGroupId: $groupId
      gedcomB64: $gedcomB64
    ) {
      id
      firstName
      middleName
      lastName
      dateOfBirth
      dateOfDeath
    }
  }
`

// Concierge
export const GetAssignedStaff = gql`
  query {
    me {
      assignedStaff {
        role
        staff {
          id
          name
          avatarUrl
          staffProfile {
            bio
            phone
            title
          }
        }
      }
    }
  }
`

// Metadata is available for any authenticated User
export const MetaData = gql`
  query {
    metadata {
      name
      version
      server_time
      obscured_ids
      firebase_project_id
      intercom_app_id
    }
  }
`

// Open an Intercom conversation with a User (Staff member) that has an Intercom ID
export const OpenIntercomConversation = gql`
  mutation ($staffId: ID!, $message: String!) {
    openIntercomConversation(staffId: $staffId, message: $message)
  }
`

// Sign Up user medication set
export const UserSetMedications = gql`
  mutation ($userId: ID!, $medications: [NewUserMedication!]!) {
    userSetMedications(userId:$userId, medications: $medications) {
      name
      strength
    }
  }
`

//* Admin Only

// A new physician becomes a dDNA Physician by associating themselves to exactly one Physician Code
// when they provide an existing, but unused Physician Code during the new Physician Onboarding
// process. [Open, no auth required]
export const PhysicianRegister = gql`
  mutation ($physicianCode: String!, $physicianData: PhysicianInput!) {
    physicianRegister(physicianCode: $physicianCode, physician: $physicianData) {
      id
      name
      staffProfile {
        npi
        phone
        specialty
        title
        clinicName
        clinicAddressLine1
        clinicAddressLine2
        clinicAddressCity
        clinicAddressState
        clinicAddressPostalCode
        clinicAddressPostalCountry
      }
      roles
    }
  }
`

// List first 10 Reports in the LOCKED status for a specific Member [Staff only]
export const Reports = gql`
  query ($first: Int!, $memberId: ID!) {
    reports(
      first: $first
      memberId: $memberId
    ) {
      pageInfo {
        hasPreviousPage
        hasNextPage
      }
      edges {
        cursor
        node {
          id
          data
          reportType {
            id
            name
          }
          locked
          createdAt
          updatedAt
          dataUpdatedAt
          version
          needsReview
          pdfUrl
        }
      }
    }
  }
`

// Review a Report [Physician Staff only]
export const ReportReview = gql`
  mutation ($reportId: ID!) {
    reportReview(reportId: $reportId) {
      id
      needsReview
      locked
      pdfUrl
      data
    }
  }
`

// Unlock a Report [Physician Staff only]
export const ReportUnReview = gql`
  mutation ($reportId: ID!) {
    reportUnreview(reportId: $reportId) {
      id
      needsReview
      locked
      pdfUrl
      data
    }
  }
`

// Lock a report [Staff only]
export const ReportLock = gql`
  mutation ($reportId: ID!) {
    reportLock(reportId: $reportId) {
      id
      version
      data
      locked
      createdAt
      updatedAt
    }
  }
`

//  Unlock a report [Staff only]
export const ReportUnLock = gql`
  mutation ($reportId: ID!) {
    reportUnlock(reportId: $reportId) {
      id
      version
      data
      locked
      createdAt
      updatedAt
    }
  }
`

// # Create a Provider Note [Staff only]
export const CreateProviderNote = gql`
  mutation ($providerNoteInput: ProviderNoteInput!) {
    providerNoteCreate (
      providerNoteInput: $providerNoteInput
    ) {
      id
      staff {
        id
      }
      member {
        id
      }
      body
      deleted
      createdAt
    }
  }
`

// # List of Provider Notes
// # 
// # Staff must query by either their own staff_id, or by member_id.
// # Members can only view their own Provider Notes that are not deleted.
// # Admin is not restricted.
export const GetProviderNotes = gql`
  query ($first: Int!, $deleted: Boolean!, $memberId: ID!) {
    providerNotes(first: $first, deleted: $deleted, memberId: $memberId) {
      pageInfo {
        hasPreviousPage
        hasNextPage
      }
      edges {
        cursor
        node {
          id
          staff {
            id
            name
            avatarUrl
          }
          member {
            id
            name
            avatarUrl
          }
          attachments {
            id
            filename
            size
            mimeType
            url
            createdAt
          }
          body
          deleted
          deletedAt
          createdAt
        }
      }
    }
  }
`

// # Delete a ProviderNote [Staff and Admin only]
// #
// # Staff can delete Provider Notes they created.
// # Admin can delete any Provider Note
export const DeleteProviderNote = gql`
  mutation ($providerNoteId: ID!) {
    providerNoteDelete(providerNoteId: $providerNoteId) {
      id
      deleted
      deletedAt
      updatedAt
      body
      staff { name avatarUrl }
      member { name }
    }
  }
`

// # Create a Provider Only Note [Staff only]
export const CreateProviderOnlyNote = gql`
  mutation ($providerOnlyNoteInput: ProviderOnlyNoteInput!) {
    providerOnlyNoteCreate (
      providerOnlyNoteInput: $providerOnlyNoteInput
    ) {
      id
      staff {
        id
      }
      member {
        id
      }
      body
      deleted
      createdAt
    }
  }
`

export const CreateProviderOnlyNoteUploadAttachment = gql`
  mutation ($providerOnlyNoteId: ID!, $uploadInput: UploadInput!) {
    providerOnlyNoteUploadAttachment(
        providerOnlyNoteId: $providerOnlyNoteId
        uploadInput: $uploadInput
    ) {
        id
        method
        url
        headers {
            key
            value
        }
    }
  }
`;

export const CreateProviderNoteUploadAttachment = gql`
  mutation ($providerNoteId: ID!, $uploadInput: UploadInput!) {
    providerNoteUploadAttachment(
        providerNoteId: $providerNoteId
        uploadInput: $uploadInput
    ) {
        id
        method
        url
        headers {
            key
            value
        }
    }
  }
`;

// # List of Provider Only Notes [Staff only]
// #
// # Staff must query by either their own staff_id, or by member_id.
// # Members cannot view any Provider Only Notes, even if the Note is associated to themselves.
// # Admin is not restricted.
export const GetProviderOnlyNotes = gql`
  query ($first: Int!, $deleted: Boolean!, $memberId: ID!) {
    providerOnlyNotes(first: $first, deleted: $deleted, memberId: $memberId) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        __typename
      }
      edges {
        cursor
        node {
          id
          staff {
            id
            name
            avatarUrl
            __typename
          }
          member {
            id
            name
            avatarUrl
            __typename
          }
          attachments {
            id
            filename
            size
            mimeType
            url
            createdAt
          }
          body    #  This is the text content of the Note
          deleted
          deletedAt
          createdAt
          __typename
        }
        __typename
      }
      __typename
    }
  }
`

// # Delete a ProviderOnlyNote [Staff and Admin only]
// #
// # Staff can delete Provider Only Notes they created.
// # Admin can delete any Provider Only Note.
export const DeleteProviderOnlyNote = gql`
  mutation ($providerOnlyNoteId: ID!) {
    providerOnlyNoteDelete(providerOnlyNoteId: $providerOnlyNoteId) {
      id
      deleted
      deletedAt
      updatedAt
      body
      staff { name avatarUrl }
      member { name }
    }
  }
`

export const AddStaffMemberOfUser = gql`
  mutation AddStaffMemberOfUser($userStaffInput: UserStaffInput!) {
    assignUserStaff(
      userStaffInput: $userStaffInput
    ) {
        id
        role
        member {
          id
          name
          dateOfBirth
          sex
          avatarUrl
          email
          phone
          createdAt
          kits { 
            accessionId 
          }
          assignedStaff {
            id
            role
            staff {
              avatarUrl
              name
              staffProfile {
                title
                specialty
                phone
                npi
                bio
              }
            }
          }
          medications {
            name
            strength
            createdAt
          }
          staffProfile {
            phone
          }
        }
      }
  }
`

export const RemoveStaffMemberOfUser = gql`
  mutation ($id: ID!) {
    delete(id: $id)
  }
`

export const PaitentMeetingCreate = gql`
  mutation PatientMeetingCreate($memberId: ID!, $staffId: ID!) {
    patientMeetingCreate(memberId: $memberId, staffId: $staffId) {
        id
        createdAt
        updatedAt
        paidAt
    }
  } 
`

export const PaitentMeetingDelete = gql`
  mutation ($id: ID!) {
    delete(id: $id)
  }
`

export const SetPatientMeetingPaid = gql`
  mutation PatientMeetingSetPaid($meetingId: ID!, $paid: Boolean!) {
    patientMeetingSetPaid(meetingId: $meetingId, paid: $paid) {
      id
      createdAt
      updatedAt
      paidAt
    }
  }
`

export const GetChangeLogData = gql`
  query($id: ID!) {
    node(id: $id) {
      ... on User {
        patientChangeLogs(first: 500) {
          edges {
            node {
              id
              change
              json
              changedBy {
                name
              }
              createdAt
            }
          }
        }
      }
    }
  }
`

export const GetStaffUsers = gql`
  query ($first: Int!, $roles: [UserRole!]) {
    users(first: $first, roles: $roles) {
      pageInfo {
        hasPreviousPage
        hasNextPage
      }
      edges {
        cursor
        node {
          id
          name
          roles
          validStaffRoles
        }
      }
    }
  }
`
export const SetKitStatusSentToBcl = gql`
  mutation ($memberId: ID!, $confirm: Boolean!) {
      kitStatusSentToBcl(memberId: $memberId, confirm: $confirm) 
  }
`
export const SetKitStatusCnvDataReceived = gql`
  mutation ($kitCode: String!, $confirm: Boolean!) {
      kitStatusCnvDataReceived(kitCode: $kitCode, confirm: $confirm) 
  }
`
export const SetKitStatusReceivedFromBcl = gql`
  mutation ($kitCode: String!, $confirm: Boolean!) {
      kitStatusReceivedFromBcl(kitCode: $kitCode, confirm: $confirm) 
  }
`
export const SetKitStatusSentToFabric = gql`
  mutation ($kitCode: String!, $confirm: Boolean!) {
      kitStatusSentToFabric(kitCode: $kitCode, confirm: $confirm) 
  }
`
export const SetKitStatusAlzheimerInfoGenerated = gql`
  mutation ($kitCode: String!, $confirm: Boolean!) {
      kitStatusAlzheimerInfoGenerated(kitCode: $kitCode, confirm: $confirm) 
  }
`
export const SetKitStatusSentForPositiveConfirmation = gql`
  mutation ($memberId: ID!, $confirm: Boolean!) {
      kitStatusSentForPositiveConfirmation(memberId: $memberId, confirm: $confirm) 
  }
`
export const GetMemberKitStatus = gql`
  query($id: ID!) {
    node(id: $id) {
      ... on User {
        kitStatusReports {
          name
          happened
          timestamp
        }
      }
    }
  }
`

export const GetGeneLinks = gql`
 query ($geneNames: [String!]) {
    geneLinks(geneNames: $geneNames) {
      edges {
        cursor
        node {
          id
          gene
          pharmGkbUrl
          monarchInitiativePharmgkbUrl
          geneCardUrl
          medlinePlusUrl
          omimUrl
          summary
          createdAt
          updatedAt
        }
      }
    }
  }
`

export const GetPatientReportsById = gql`
  query GetUserNode($id: ID!) {
    node(id: $id) {
      id
      ... on User {
        kits {
          id
          status
          accessionId
          description
          createdAt
          updatedAt
        }
        reportsByType {
          report {
            id
            reportTypeId
            version
            data
            pdfUrl
            locked
            createdAt
            updatedAt
            dataUpdatedAt
            needsReview
            reportType {
              id
              name
              needsReview
              locked
            }
            kit {
              id,
              accessionId
            }
          }
        }
      }
    }
  }
`;

export const GetPatientKitStatus = gql`
  query GetUserNode($id: ID!) {
    node(id: $id) {
      id
      ... on User {
        id
        email
        name
        firstName
        lastName
        middleName
        sex
        dateOfBirth
        phone
        roles
        holdEmails
        validStaffRoles
        intercomHash
        intercomId
        avatarUrl
        createdAt
        kitStatusReports {
            name
            happened
            timestamp
        }
      }
    }
  }
`;

export const OrderKit = gql`
   mutation {
    requestMoreKits
  }
`

// Physician Kit Price Query
export const GetPhysicianKitPrice = gql`
  query GetPhysicianKitPrice {
    physicianKitPrice {
      id
      physicianId
      priceCents
      priceDollars
      active
      notes
      familyDiscountEnabled
      familyDiscountPriceCents
      familyDiscountPriceDollars
    }
  }
`

// Create Pending Patient Mutation (new invite-based flow)
// Creates a pending patient record and sends an invite email to the patient
// The patient will receive a link to complete payment on the Patient Portal
// Matches backend API spec in portal-web/example_queries.graphql
export const CreatePendingPatient = gql`
  mutation CreatePendingPatient($input: PatientCheckoutInput!) {
    createPendingPatient(input: $input) {
      success
      inviteUrl
      emailSentTo
    }
  }
`

// Verify Kit Code Query (Open, no auth required)
// Used to validate a kit code before adding a patient
export const VerifyKitCode = gql`
  query VerifyKitCode($kitCode: String!) {
    verifyKitCode(kitCode: $kitCode)
  }
`

// Get Pending Patients Query
// Returns pending patients created by the current physician (or all for admin)
// Status values: PENDING, COMPLETED, EXPIRED, CANCELLED, FAILED
export const GetPendingPatients = gql`
  query GetPendingPatients($first: Int, $after: String, $status: PendingPatientStatusFilter) {
    pendingPatients(first: $first, after: $after, status: $status) {
      edges {
        cursor
        node {
          id
          status
          name
          firstName
          lastName
          email
          phoneNumber
          dateOfBirth
          sex
          kitCode
          physicianNotes
          emailLastSentAt
          reminderCount
          updatedAt
          createdAt
          physician {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`

// Update Pending Patient Mutation
// Allows Staff/Fellow to edit a pending patient's contact info, name, clinical info, and notes
// Uses input object pattern: { id, firstName?, lastName?, email?, phoneNumber?, dateOfBirth?, sex?, physicianNotes? }
// Email change triggers new invite and invalidates old links (rate limited: 1/hour)
export const UpdatePendingPatient = gql`
  mutation UpdatePendingPatient($input: UpdatePendingPatientInput!) {
    updatePendingPatient(input: $input) {
      id
      status
      name
      firstName
      lastName
      email
      phoneNumber
      dateOfBirth
      sex
      kitCode
      physicianNotes
      emailLastSentAt
      reminderCount
      updatedAt
      createdAt
      physician {
        id
        name
      }
    }
  }
`

// Resend Pending Patient Invite Email
// Rate limited: max 1 email per hour (checks emailLastSentAt)
// Only works for pending patients with PENDING status
export const ResendPendingPatientInvite = gql`
  mutation ResendPendingPatientInvite($pendingPatientId: ID!) {
    resendPendingPatientInvite(pendingPatientId: $pendingPatientId) {
      id
      email
      emailLastSentAt
      reminderCount
      updatedAt
    }
  }
`

// Cancel Pending Patient Invitation
// Only works for pending patients with PENDING status
export const CancelPendingPatient = gql`
  mutation CancelPendingPatient($pendingPatientId: ID!) {
    cancelPendingPatient(pendingPatientId: $pendingPatientId) {
      id
      email
      name
      status
      updatedAt
    }
  }
`

// Sale Creation Mutation
export const CreateSale = gql`
  mutation CreateSale($sale: SaleInput!) {
    saleCreate(sale: $sale) {
      id
      status
      totalPrice
      totalPriceDollars
      stripePaymentIntentId
      stripeCustomer
      createdAt
    }
  }
`